import { cloneDeep } from 'lodash';


import { BaseFieldFragment } from '../../../generated/graphql.generated';
import { Mutable } from '../../../typescript';
import { FIELD_CONFIG } from '../../global.constants';
import { safeParseJSON } from '../../js';
import { generateUUID } from '../../utilities/state.util';

import { InventoryActions } from './inventory.actions';

export interface InventoryItemDefinition extends Omit<InventoryItem, 'id' | 'quantity'> {}
export interface InventoryRoom {
	id: string;

	name: string;
	packing?: any;
	level?: string;
	flights?: string;
	notes?: string;
	moverNotes?: string;
	notMoving?: string;
	isCollapsed?: boolean;

	totalVolume: number;
	totalWeight: number;
	totalItems: number;
	totalBoxes: number;

	inventory: InventoryItem[];
}

export interface InventoryItem {
	id: string;
	isBulky?: boolean;
	isValuable?: boolean;
	isSpecialEquipment?: boolean;
	is3rdPartyServicing?: boolean;
	isProGear?: boolean;
	isAssembleDisassemble?: boolean;
	isCarrierPack?: boolean;
	isoCode1?: string;
	isoCode2?: string;
	isoCode3?: string;
	reviewerName?: string;
	name: string;
	label?: string;
	packing?: string;
	quantity: number;

	volume?: number;
	weight?: number;
	totalVolume?: number;
	totalWeight?: number;

	generated?: boolean;
}

export const defaultInventory = {
	totalVolume: 0 as number,
	totalItems: 0 as number,
	totalWeight: 0 as number,

	totalBoxes: 0 as number,

	rooms: [] as InventoryRoom[],
	specialtyItems: [] as string[],

	weightUnit: 'lbs',
	volumeUnit: 'ft³',
} as const;

export type JobInventory = Mutable<typeof defaultInventory>;

export function addItemDefinitionToRoom(stateRooms: InventoryRoom[], item: InventoryItemDefinition, roomId: string) {

	const rooms = cloneDeep(stateRooms);
	const room = rooms.find((r) => r.id === roomId);
	if (!room || !item) {
		return rooms;
	}

	room.inventory.push({
		...item,
		id: generateUUID(),
		name: item.reviewerName || item.label,
		generated: true,
		quantity: 1,
		volume: parseFloat(item.volume as any),
		weight: parseFloat(item.weight as any),
	});

	return rooms;
}


export function addCustomItemToRoom(stateRooms: InventoryRoom[], search: string, roomId: string) {

	const rooms = cloneDeep(stateRooms);
	const room = rooms.find((r) => r.id === roomId);
	if (!room || !search) {
		return rooms;
	}

	room.inventory.push({
		id: generateUUID(),
		name: search,
		quantity: 1,
		reviewerName: search,
		label: search,
		volume: 0,
		weight: 0,
		// isoCode1: "000",
		// isoCode2: "000",
		// isoCode3: "000",
		// isBulky: false,
		// isValuable: false,
		// isSpecialEquipment: false,
		// is3rdPartyServicing: false,
		// isProGear: false,
		// isAssembleDisassemble: false,
		generated: false,
	});

	return rooms;
}

export function parseInventoryFromFields(
	fields: Partial<BaseFieldFragment>[],
) {
	if (!fields?.length) {
		return {
			inventory: defaultInventory,
			notes: undefined,
		};
	}

	const inventoryField = fields?.find((f) => f.name === FIELD_CONFIG.jobs.inventory.name);
	let inventory: JobInventory = safeParseJSON(inventoryField?.values[0]?.value, defaultInventory);
	inventory = recalculateInventory(inventory.rooms, inventory.weightUnit, inventory.volumeUnit);

	const inventoryNotesField = fields?.find((f) => f.name === FIELD_CONFIG.jobs.inventoryNotes.name);
	const inventoryNotes = inventoryNotesField?.values[0]?.value;

	return {
		inventory,
		notes: inventoryNotes,
	};
}

export function recalculateInventory(
	rooms: InventoryRoom[],
	weightUnit: JobInventory['weightUnit'],
	volumeUnit: JobInventory['volumeUnit'],
) {
	const inventory: JobInventory = {
		rooms: cloneDeep(rooms),
		weightUnit,
		volumeUnit,
		specialtyItems: [],
		totalBoxes: 0,
		totalItems: 0,
		totalVolume: 0,
		totalWeight: 0,
	};

	for (const room of inventory.rooms) {
		room.totalItems = 0;
		room.totalBoxes = 0;
		room.totalVolume = 0;
		room.totalWeight = 0;
		room.id = room.id || generateUUID();

		for (const item of room.inventory) {
			item.weight = parseFloat(item.weight as any);
			item.volume = parseFloat(item.volume as any);
			item.quantity = parseFloat(item.quantity as any) || 0;
			item.totalWeight = item.weight * item.quantity;
			item.totalVolume = item.volume * item.quantity;
			room.totalWeight += item.totalWeight;
			room.totalVolume += item.totalVolume;
			room.totalItems += item.quantity;
			item.id = item.id || generateUUID();
		}

		inventory.totalWeight += room.totalWeight;
		inventory.totalVolume += room.totalVolume;
		inventory.totalItems += room.totalItems;
	}

	return inventory;
}
